import React from 'react';
import { Skill } from '../types';
import './SkillIcon.css';

interface SkillIconProps {
  skill: Skill;
  size?: number;
  isOnCooldown?: boolean;
  cooldownRemaining?: number;
  keyBinding?: string;
  onClick?: () => void;
  highlightType?: 'none' | 'combo' | 'proc' | 'resource' | 'ready';
  highlightStyle?: any;
  isHighlighted?: boolean;
  isDisabled?: boolean;
  showDetailedTooltip?: boolean;
  onMouseEnter?: (event: React.MouseEvent, skill: Skill) => void;
  onMouseLeave?: () => void;
  chargeInfo?: { current: number; max: number; nextChargeIn: number } | null;
}

export const SkillIcon: React.FC<SkillIconProps> = ({ 
  skill, 
  size = 40, 
  isOnCooldown = false,
  cooldownRemaining = 0,
  keyBinding,
  onClick,
  highlightType = 'none',
  highlightStyle,
  isHighlighted = false,
  isDisabled = false,
  showDetailedTooltip = false,
  onMouseEnter,
  onMouseLeave,
  chargeInfo
}) => {
  const getSkillTypeColor = () => {
    return '#4A90E2'; // 统一的技能颜色
  };

  const getHighlightStyle = () => {
    // 如果传入了外部高亮样式，优先使用
    if (highlightStyle && highlightType !== 'none') {
      return {
        ...highlightStyle,
        animation: 'pulse 1.5s infinite'
      };
    }

    // 后备的内部高亮样式
    if (!isHighlighted || highlightType === 'none') {
      return {};
    }

    const baseStyle = {
      animation: 'pulse 1.5s infinite',
      filter: 'brightness(1.2)'
    };

    switch (highlightType) {
      case 'combo':
        return {
          ...baseStyle,
          boxShadow: '0 0 15px #FFD700, 0 0 25px rgba(255, 215, 0, 0.5)',
          border: '3px solid #FFD700'
        };
      case 'proc':
        return {
          ...baseStyle,
          boxShadow: '0 0 15px #00FF7F, 0 0 25px rgba(0, 255, 127, 0.5)',
          border: '3px solid #00FF7F'
        };
      case 'resource':
        return {
          ...baseStyle,
          boxShadow: '0 0 15px #1E90FF, 0 0 25px rgba(30, 144, 255, 0.5)',
          border: '3px solid #1E90FF'
        };
      case 'ready':
        return {
          ...baseStyle,
          boxShadow: '0 0 15px #FFA500, 0 0 25px rgba(255, 165, 0, 0.5)',
          border: '3px solid #FFA500'
        };
      default:
        return {};
    }
  };

  return (
    <div 
      style={{
        position: 'relative',
        width: size,
        height: size,
        cursor: onClick && !isDisabled ? 'pointer' : 'default',
        opacity: isDisabled ? 0.3 : (isOnCooldown ? 0.6 : 1),
        borderRadius: '4px',
        filter: isDisabled ? 'grayscale(100%) brightness(0.7)' : 'none',
        ...(!isDisabled ? getHighlightStyle() : {}) // 禁用时不显示高亮
      }}
      onClick={onClick}
      title={showDetailedTooltip ? undefined : `${skill.name}\n${skill.description}`}
      onMouseEnter={onMouseEnter ? (e) => onMouseEnter(e, skill) : undefined}
      onMouseLeave={onMouseLeave}
    >
      {skill.icon ? (
        <img 
          src={skill.icon} 
          alt={skill.name}
          width={size} 
          height={size}
          style={{
            borderRadius: '4px',
            border: '2px solid #333',
            pointerEvents: 'none'
          }}
          onError={(e) => {
            // 如果图片加载失败，显示备用SVG
            e.currentTarget.style.display = 'none';
            const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
            if (nextElement) {
              nextElement.style.display = 'block';
            }
          }}
        />
      ) : null}
      <svg 
        width={size} 
        height={size} 
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: skill.icon ? 'none' : 'block' }}
      >
        <rect 
          width="38" 
          height="38" 
          x="1" 
          y="1" 
          fill={getSkillTypeColor()} 
          stroke="#333" 
          strokeWidth="2"
          rx="4"
        />
        <text 
          x="20" 
          y="14" 
          textAnchor="middle" 
          fontSize="8" 
          fill="white" 
          fontWeight="bold"
        >
          {skill.name.substring(0, 2)}
        </text>
        <text 
          x="20" 
          y="26" 
          textAnchor="middle" 
          fontSize="6" 
          fill="#ccc"
        >
          100
        </text>
      </svg>
      
      {isOnCooldown && cooldownRemaining > 0 && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          color: 'white',
          fontSize: '12px',
          fontWeight: 'bold',
          borderRadius: '4px'
        }}>
          {cooldownRemaining.toFixed(1)}
        </div>
      )}
      
      {keyBinding && (
        <div style={{
          position: 'absolute',
          bottom: '-16px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          color: 'white',
          padding: '1px 4px',
          borderRadius: '2px',
          fontSize: '10px',
          fontWeight: 'bold'
        }}>
          {keyBinding}
        </div>
      )}

      {chargeInfo && chargeInfo.max > 1 && (
        <div style={{
          position: 'absolute',
          top: '2px',
          right: '2px',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          color: chargeInfo.current > 0 ? '#00FF00' : '#FF0000',
          padding: '1px 4px',
          borderRadius: '2px',
          fontSize: '10px',
          fontWeight: 'bold',
          minWidth: '16px',
          textAlign: 'center'
        }}>
          {chargeInfo.current}
        </div>
      )}
      
    </div>
  );
};